import { useState, useLayoutEffect, useCallback } from "react";

const GAP = 10;

const placements = [
  "top",
  "bottom",
  "right",
  "left"
];

export default function useTooltipPosition({
  targetRef,
  tooltipRef,
  placement = "top",
  offset = GAP,
  visible
}) {
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement
  });

  const calculatePosition = useCallback(() => {
    if (!targetRef.current || !tooltipRef.current) return;

    const target = targetRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const calculate = (currentPlacement) => {
      let top = 0;
      let left = 0;

      switch (currentPlacement) {
        case "top":
          top = target.top - tooltip.height - offset;
          left = target.left + target.width / 2 - tooltip.width / 2;
          break;

        case "bottom":
          top = target.bottom + offset;
          left = target.left + target.width / 2 - tooltip.width / 2;
          break;

        case "left":
          top = target.top + target.height / 2 - tooltip.height / 2;
          left = target.left - tooltip.width - offset;
          break;

        case "right":
          top = target.top + target.height / 2 - tooltip.height / 2;
          left = target.right + offset;
          break;

        case "top-start":
          top = target.top - tooltip.height - offset;
          left = target.left;
          break;

        case "top-end":
          top = target.top - tooltip.height - offset;
          left = target.right - tooltip.width;
          break;

        case "bottom-start":
          top = target.bottom + offset;
          left = target.left;
          break;

        case "bottom-end":
          top = target.bottom + offset;
          left = target.right - tooltip.width;
          break;

        case "left-start":
          top = target.top;
          left = target.left - tooltip.width - offset;
          break;

        case "left-end":
          top = target.bottom - tooltip.height;
          left = target.left - tooltip.width - offset;
          break;

        case "right-start":
          top = target.top;
          left = target.right + offset;
          break;

        case "right-end":
          top = target.bottom - tooltip.height;
          left = target.right + offset;
          break;

        default:
          break;
      }

      return { top, left };
    };

    const fits = ({ top, left }) => ({
      top: top >= 0,
      bottom: top + tooltip.height <= viewportHeight,
      left: left >= 0,
      right: left + tooltip.width <= viewportWidth
    });

    let preferred = placement;

    if (preferred === "auto") {
      preferred = "top";
    }

    let result = calculate(preferred);

    let visibleArea = fits(result);

    if (
      !(
        visibleArea.top &&
        visibleArea.bottom &&
        visibleArea.left &&
        visibleArea.right
      )
    ) {
      for (const next of placements) {
        const candidate = calculate(next);
        const ok = fits(candidate);

        if (
          ok.top &&
          ok.bottom &&
          ok.left &&
          ok.right
        ) {
          result = candidate;
          preferred = next;
          break;
        }
      }
    }

    let finalTop = result.top;
    let finalLeft = result.left;

    // Keep inside viewport

    finalLeft = Math.max(
      8,
      Math.min(
        finalLeft,
        viewportWidth - tooltip.width - 8
      )
    );

    finalTop = Math.max(
      8,
      Math.min(
        finalTop,
        viewportHeight - tooltip.height - 8
      )
    );

    setPosition({
      top: finalTop,
      left: finalLeft,
      placement: preferred
    });
  }, [
    placement,
    offset,
    targetRef,
    tooltipRef
  ]);

  useLayoutEffect(() => {
    if (!visible) return;

    calculatePosition();

    window.addEventListener("resize", calculatePosition, true);
    window.addEventListener("scroll", calculatePosition, true);

    return () => {
      window.removeEventListener(
        "resize",
        calculatePosition,
        true
      );

      window.removeEventListener(
        "scroll",
        calculatePosition,
        true
      );
    };
  }, [visible, calculatePosition]);

  return position;
}